import { createApi } from "@reduxjs/toolkit/query/react";

import { safeInvalidateTags } from "@/providers/store/api/safe-invalidate-tags";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { setUser } from "../../slices/user/slice";
import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import { type IChangePassword, type IUpdateProfile, type IUser } from "./users.interface";

const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Users],
  endpoints: (builder) => ({
    getUser: builder.query<IUser, null>({
      query: () => ({
        url: "/users/profile",
        method: "GET",
      }),
      providesTags: [ApiTags.Users],
      transformResponse: (result: IApiResponse<IUser>) => result.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(setUser(data));
      },
    }),
    updateProfile: builder.mutation<void, IUpdateProfile>({
      query: ({
        firstName, lastName,
      }) => ({
        url: "/users/profile",
        method: "PUT",
        body: {
          firstName,
          lastName,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.Users]),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.resetApiState());
        await dispatch(usersApi.endpoints.getUser.initiate(null));
      },
    }),
    changePassword: builder.mutation<void, IChangePassword>({
      query: ({
        currentPassword, newPassword,
      }) => ({
        url: "/users/profile/change-password",
        method: "POST",
        body: {
          currentPassword,
          newPassword,
        },
      }),
    }),
  }),
});

export const {
  useGetUserQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
} = usersApi;
export default usersApi;
